<template>
  <v-card>
    <v-card-title>
      {{ $t('lds.addLiquidatedDamage') }}
    </v-card-title>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col>
            <PartsSelect
              v-model="part"
              :parts="partsList"
              :isMultiCurrency="isMultiCurrency"
              :disabled="isFinalized"
            />
          </v-col>
          <v-col>
            <v-select
              v-model="form.status"
              :items="statusesList"
              :label="$t('common.status')"
              dense
              outlined
              :error-messages="errorMessages('status')"
              @change="$v.form.status.$touch()"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.name"
              :label="$t('lds.name')"
              counter="64"
              dense
              outlined
              :error-messages="errorMessages('name')"
              @change="$v.form.name.$touch()"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.milestoneName"
              :label="$t('lds.milestoneName')"
              counter="16"
              dense
              outlined
              :disabled="isFinalized"
              :error-messages="[...errorMessages('milestoneName'), duplicateMilestoneNameError].filter(i => !!i)"
              @change="$v.form.milestoneName.$touch()"
            />
          </v-col>
          <v-col md="6">
            <v-text-field
              v-model.number="form.documentUrl"
              :label="$t('lds.documentUrl')"
              :hint="$t('lds.documentUrlHint')"
              outlined
              dense
              persistent-hint
              :error-messages="errorMessages('documentUrl')"
              @change="$v.form.documentUrl.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="form.description"
              :label="$t('common.notes')"
              dense
              outlined
              counter="128"
              rows="2"
              :error-messages="errorMessages('description')"
              @change="$v.form.description.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VfDateInput
              v-model="form.originalDate"
              :label="$t('lds.originalDate')"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('originalDate')"
              @change="$v.form.originalDate.$touch()"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model.number="form.eot"
              :label="$t('lds.eot')"
              type="number"
              :min="0"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('eot')"
              @change="$v.form.eot.$touch()"
            />
          </v-col>
          <v-col>
            <VfDateInput
              v-model="form.amendedCompletionDate"
              :label="$t('lds.amendedDate')"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('amendedCompletionDate')"
              @change="$v.form.amendedCompletionDate.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VfDateInput
              v-model="form.actualCompletionDate"
              :label="$t('lds.actualDate')"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('actualCompletionDate')"
              @change="$v.form.actualCompletionDate.$touch()"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model.number="form.delay"
              :label="$t('lds.delay')"
              type="number"
              :min="0"
              outlined
              dense
              prepend-icon="mdi-calendar-end"
              :disabled="isFinalized"
              :error-messages="errorMessages('delay')"
              @change="$v.form.delay.$touch()"
            >
              <template v-slot:append-outer>
                <InfoTooltip :text="$t('lds.delayInfo')" size="normal"/>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VfCurrencyInput
              v-model="form.totalValueCents"
              :label="$t('lds.totalValue')"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('totalValueCents')"
              :currency="currentCurrency"
              @change="$v.form.totalValueCents.$touch()"
            />
          </v-col>
          <v-col>
            <VfCurrencyInput
              v-model="form.capValueCents"
              :label="$t('lds.capValue')"
              outlined
              dense
              :disabled="isFinalized"
              :error-messages="errorMessages('capValueCents')"
              :currency="currentCurrency"
              @change="$v.form.capValueCents.$touch()"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-btn
        v-if="isEditMode && !isFinalized && isValueHigherThenZero"
        rounded
        dark
        dense
        small
        color="primary"
        @click="handleFinalize"
      >
        {{ $t('common.finalize') }}
        <v-icon class="ml-1" small>mdi-check</v-icon>
      </v-btn>
      <v-btn
        v-if="isEditMode && isFinalized"
        rounded
        dark
        dense
        small
        color="orange"
        @click="handleUnlock"
      >
        {{ $t('common.unlock') }}
        <v-icon class="ml-1" small>mdi-lock-open-outline</v-icon>
      </v-btn>
      <v-spacer />
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="handleCancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton @click="handleSubmit" />
    </v-card-actions>
  </v-card>
</template>

<script>
import dayjs from 'dayjs'
import _ from 'lodash'
import { DEFAULT_LD, LD_STATUSES } from '../../helpers'
import { finalizeLiquidatedDamage, unlockLiquidatedDamage } from '../../services'
import { errorMessageGenerator, liquidatedDamageModel } from '../../validators'
import { getLocalizationContext } from '../../helpers/translations'

export default {
  name: 'LiquidatedDamagesForm',
  props: {
    liquidatedDamage: Object,
    parts: Array,
    currencies: Array,
    contractId: Number,
    isMultiCurrency: Boolean,
    isMilestoneNameDuplicate: Function
  },
  data () {
    return {
      form: _.cloneDeep(this.getInitialForm()),
      part: null,
      statusesList: LD_STATUSES
    }
  },
  created () {
    const partId = this.liquidatedDamage ? this.liquidatedDamage.partId : null
    this.part = partId ? this.partsList.find(p => p.id === partId) : this.partsList[0]
  },
  computed: {
    isEditMode () {
      return !!this.form.id
    },
    isFinalized () {
      return this.liquidatedDamage ? this.liquidatedDamage.finalized : false
    },
    isValueHigherThenZero () {
      return this.liquidatedDamage ? !!this.liquidatedDamage.totalValueCents : false
    },
    partsList () {
      const formattedParts = this.parts.map(part => {
        return {
          ...part,
          formattedPrice: this.$options.filters.formatCurrency(part.signedPriceCents, { key: part.currency, value: 1 })
        }
      })
      const allSitesOption = { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), partId: null, disabled: this.isMultiCurrency }
      return this.isMultiCurrency ? formattedParts : [allSitesOption, ...formattedParts]
    },
    currentCurrency () {
      const currency = this.part.currency ? this.currencies.find(currency => currency.key === this.part.currency) : this.currencies[0]
      return { ...currency, value: 1 }
    },
    duplicateMilestoneNameError () {
      const initialName = this.isEditMode ? this.liquidatedDamage.milestoneName : ''
      return this.isMilestoneNameDuplicate(this.form.milestoneName, initialName) && !this.isFinalized ? this.$t('lds.duplicateName') : null
    }
  },
  methods: {
    syncDelayFromDates () {
      const baseDate = this.form.amendedCompletionDate || this.form.originalDate
      if (!baseDate || !this.form.actualCompletionDate) {
        return
      }
      const baseline = dayjs(baseDate).startOf('day')
      const actual = dayjs(this.form.actualCompletionDate).startOf('day')
      if (!baseline.isValid() || !actual.isValid()) {
        return
      }
      const diffDays = actual.diff(baseline, 'day')
      this.form.delay = Math.max(0, diffDays)
    },
    handleCancel () {
      this.$eventBus.$emit('CLOSE_MODAL')
    },
    async handleSubmit () {
      await this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('SAVE_LD', this.form)
        this.$eventBus.$emit('CLOSE_MODAL')
      }
    },
    handleFinalize () {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('lds.finalizeConfirm'),
          subtitle: this.$t('lds.finalizeConfirmMessage')
        },
        handlers: {
          yes: async () => {
            await finalizeLiquidatedDamage(this.form, this.contractId)
            this.$eventBus.$emit('CLOSE_MODAL')
            this.$emit('FINALIZE_LD')
          }
        }
      })
    },
    handleUnlock () {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('lds.unlockConfirm'),
          subtitle: this.$t('lds.unlockConfirmMessage')
        },
        handlers: {
          yes: async () => {
            await unlockLiquidatedDamage(this.form, this.contractId)
            this.$eventBus.$emit('CLOSE_MODAL')
            this.$emit('UNLOCK_LD')
          }
        }
      })
    },
    getInitialForm () {
      if (this.liquidatedDamage) {
        const { liquidatedDamageDates, ...form } = this.liquidatedDamage
        return form
      }
      return DEFAULT_LD
    },
    errorMessages (key) {
      return errorMessageGenerator(this.$v.form[key], key)
    }
  },
  watch: {
    part (value) {
      this.form.partId = value ? value.id : null
    },
    'form.originalDate': function () {
      this.syncDelayFromDates()
    },
    'form.amendedCompletionDate': function () {
      this.syncDelayFromDates()
    },
    'form.actualCompletionDate': function () {
      this.syncDelayFromDates()
    }
  },
  validations: liquidatedDamageModel
}
</script>
